import { Heading } from "@/components/ui/heading";
import { RegularPageLayout } from "@/components/layout/PageLayouts";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DEFAULT_API_URL } from "../lib/const";

type HealthResponse = {
	status?: string;
};

type StatusState = "loading" | "ok" | "error";

const API_URL = import.meta.env.VITE_API_URL || DEFAULT_API_URL;
export default function StatusPage() {
	const [state, setState] = useState<StatusState>("loading");
	const [message, setMessage] = useState("Checking API health...");

	useEffect(() => {
		let cancelled = false;

		async function checkHealth() {
			try {
				setState("loading");
				setMessage("Checking API health...");

				const response = await fetch(`${API_URL}/health`);
				const payload = (await response.json().catch(() => ({}))) as HealthResponse;

				if (!response.ok) {
					throw new Error(`Health endpoint returned ${response.status}.`);
				}

				if (payload.status === "ok") {
					if (!cancelled) {
						setState("ok");
						setMessage("All systems are running normally.");
					}
					return;
				}

				throw new Error("Health endpoint did not return expected status.");
			} catch (error) {
				if (!cancelled) {
					const reason = error instanceof Error ? error.message : "Unknown error";
					setState("error");
					setMessage(`API health check failed: ${reason}`);
				}
			}
		}

		void checkHealth();
		return () => {
			cancelled = true;
		};
	}, []);

	const statusCard = useMemo(() => {
		if (state === "loading") {
			return (
				<div className="mt-6 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-slate-700">
					<Loader2 className="h-5 w-5 animate-spin text-slate-500" />
					<p className="text-sm sm:text-base">{message}</p>
				</div>
			);
		}

		if (state === "ok") {
			return (
				<div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4">
					<div className="flex items-center gap-3">
						<CheckCircle2 className="h-6 w-6 text-emerald-600" />
						<div>
							<p className="font-semibold text-emerald-900">Operational</p>
							<p className="text-sm text-emerald-800 sm:text-base">{message}</p>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-4">
				<div className="flex items-center gap-3">
					<AlertTriangle className="h-6 w-6 text-rose-600" />
					<div>
						<p className="font-semibold text-rose-900">Issue detected</p>
						<p className="text-sm text-rose-800 sm:text-base">{message}</p>
						<p className="mt-1 text-sm text-rose-800 sm:text-base">
							We are working to resolve this as soon as possible.
						</p>
					</div>
				</div>
			</div>
		);
	}, [message, state]);

	return (
		<RegularPageLayout width="6xl" paddingY="8">
			<section className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
				<Heading>Status</Heading>
				<p className="mt-6 text-sm leading-7 text-slate-700 sm:text-base">
					This page shows the current availability of Text Formatter services.
				</p>
				{statusCard}
			</section>
		</RegularPageLayout>
	);
}
